import React, { useEffect, useState, useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import ContentLayout from 'src/components/ContentLayout';
import SBPLogo from 'src/components/SBPLogo';
import Button from 'src/components/Button';
import Text from 'src/components/Text';
import Link from 'src/components/Link';
import PageSpin from 'src/components/PageSpin';
import HelpLink from 'src/components/HelpLink';
import routes from 'src/constants/routes';
import { useNavigate } from 'src/utils/useNavigate';
import { useSearchParams } from 'src/utils/useSearchParams';
import { getAuthorizationStateAPI } from 'src/serverApi/authorization';
import useApplicationAllowedBanks from 'src/dataHooks/useApplicationAllowedBanks';
import SBPLogoRaiffeisen from './customizations/SBPLogoRaiffeisen';
import SBPLogoGenbank from './customizations/SBPLogoGenbank';
import SBPLogoTinkoff from './customizations/SBPLogoTinkoff';
import styles from './styles.module.scss';

const customizationToLogoComponent: { [x: string]: React.FC<{ className?: string }> } = {
  raiffeisen: SBPLogoRaiffeisen,
  genbank: SBPLogoGenbank,
  tinkoff : SBPLogoTinkoff,
}

const Main: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customization = searchParams.get('customization') || '';
  const { fetchAllowedBanks, isAllowedBanksLoading, allowedBanks } = useApplicationAllowedBanks();
  const [customizationBank, setCustomizationBank] = useState<any>();
  
  useEffect(() => {
    if (customization) {
      fetchAllowedBanks(customization);
    }
  }, [customization]);
  
  useEffect(() => {
    if (allowedBanks) {
      const banks = allowedBanks?.partnerBanks || [];
      setCustomizationBank(banks[0]);
    }
  }, [allowedBanks]);
  
  const Logo = customizationToLogoComponent[customizationBank?.islMemberId?.toLowerCase()] || SBPLogo;
  
  const bankApplicationText = useMemo(() => {
    if (!customizationBank) {
      return (
        <>
          <Text type="p" muted>
            Агент 1С:СБП помогает получить токены для настройки оплат через СБП в учетных программах.
          </Text>
          <Text type="p" muted>
            Перед получением токенов, нужно заключить договор на СБП с банком. Просмотреть список банков, которые
            поддерживают работу через Агент 1С:СБП, и отправить заявку на заключение договора с банком-партнером можно
            на странице{' '}
            <Link to={routes.banks}>
              по&nbsp;ссылке
            </Link>.
          </Text>
        </>
      );
    }
    
    return (
      <>
        <Text type="p" muted>
          Агент 1С:СБП помогает получить токены для настройки оплат через СБП в учетных программах.
          Для получения токена войдите или зарегистрируйтесь в Агенте 1С:СБП.
        </Text>
        <Text type="p" muted>
          Перед получением токенов, нужно заключить договор на СБП с банком.
        </Text>
        <Text type="p" muted>
          Вы можете отправить заявку на заключение договора в {customizationBank?.name || ''}{' '}
          <Link to={generatePath(routes.bankApplication, { bankUeid: customizationBank?.ueid || '' })}>
            по&nbsp;ссылке
          </Link>.
        </Text>
      </>
    )
  }, [customizationBank]);
  
  const authorize = async () => {
    try {
      const authorizationState = await getAuthorizationStateAPI();
      if (authorizationState?.userId) {
        navigate(routes.agent);
      } else {
        navigate(routes.authorization);
      }
    } catch (error) {
      navigate(routes.authorization);
    }
  }

  const handleAuthorizationClick = () => {
    authorize();
  };
  
  const handleRegistrationClick = () => {
    navigate(routes.registration);
  };
  
  if (customization && isAllowedBanksLoading) {
    return <PageSpin />
  }

  return (
    <ContentLayout size="small">
      <HelpLink className={styles['help']}/>
      <Logo className={styles['sbp-logo']} />
      <Text type="p">
        Система быстрых платежей (СБП) — это сервис Банка России, с помощью которого покупатели могут оплачивать
        товары или услуги через свои мобильные банковские приложения.
      </Text>
      {bankApplicationText}
      <div className={styles['button-wrapper']}>
        <Button onClick={handleAuthorizationClick} >
          Войти
        </Button>
        <Button variant="outlined" onClick={handleRegistrationClick} className={styles['register-button']}>
          Зарегистрироваться
        </Button>
      </div>
    </ContentLayout>
  );
};

export default Main;
